import DOMPurify from 'dompurify'

const ALLOWED_TAGS = [
  'p', 'br', 'div', 'span', 'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'sub', 'sup',
  'a', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'pre', 'code'
]
const ALLOWED_ATTR = ['class', 'style', 'href', 'target', 'rel']
const ALLOWED_URI_REGEXP = /^(?:https?:|mailto:|tel:|#|\/)/i

const SAFE_CLASS = /^ql-[a-z0-9-]+$/
const SAFE_STYLE_PROPS = ['color', 'background-color', 'font-size', 'font-family', 'text-align', 'direction']
const SAFE_STYLE_VALUE = /^[#a-z0-9(),.%\s'"-]+$/i
const UNSAFE_STYLE_VALUE = /url\s*\(|expression|@import|javascript:|\\|</i

function sanitizeStyle (style) {
  return style
    .split(';')
    .map(declaration => {
      const index = declaration.indexOf(':')
      if (index === -1) return []
      return [declaration.slice(0, index).trim().toLowerCase(), declaration.slice(index + 1).trim()]
    })
    .filter(([prop, value]) =>
      SAFE_STYLE_PROPS.includes(prop) &&
      value &&
      SAFE_STYLE_VALUE.test(value) &&
      !UNSAFE_STYLE_VALUE.test(value))
    .map(([prop, value]) => `${prop}: ${value}`)
    .join('; ')
}

function sanitizeNode (node) {
  if (node.hasAttribute('class')) {
    const classes = node.getAttribute('class').split(/\s+/).filter(name => SAFE_CLASS.test(name))
    if (classes.length) {
      node.setAttribute('class', classes.join(' '))
    } else {
      node.removeAttribute('class')
    }
  }

  if (node.hasAttribute('style')) {
    const style = sanitizeStyle(node.getAttribute('style'))
    if (style) {
      node.setAttribute('style', style)
    } else {
      node.removeAttribute('style')
    }
  }

  if (node.tagName === 'A') {
    const target = node.getAttribute('target')
    if (target && !['_blank', '_self'].includes(target)) {
      node.removeAttribute('target')
    }
    if (node.getAttribute('target') === '_blank') {
      node.setAttribute('rel', 'noopener noreferrer')
    }
  }
}

let purify

function getPurify () {
  if (purify !== undefined) return purify

  purify = null
  if (typeof window !== 'undefined' && typeof window.document !== 'undefined') {
    // Own instance: hooks do not leak to other DOMPurify users
    const instance = DOMPurify(window)
    if (instance.isSupported) {
      instance.addHook('afterSanitizeAttributes', sanitizeNode)
      purify = instance
    }
  }

  return purify
}

/**
 * Sanitizes untrusted HTML (e.g. text component content) using an allowlist.
 * Fails closed: returns an empty string when there is no DOM available.
 */
export function sanitizeHtml (dirty) {
  if (typeof dirty !== 'string' || dirty === '') return ''

  const instance = getPurify()
  if (!instance) return ''

  return instance.sanitize(dirty, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
    ALLOW_ARIA_ATTR: false,
    ALLOWED_URI_REGEXP,
    // Without this, a custom ALLOWED_URI_REGEXP would also be applied to target/rel values and drop them
    ADD_URI_SAFE_ATTR: ['target', 'rel']
  })
}

export default sanitizeHtml
